import Button from '@/components/Button';
import Page from '@/components/page/Page';
import { FiAlertCircle } from '@/components/react-icons/main';
import IconV2 from '@/components/v2/IconV2';
import setQuery from '@/lib/setQuery';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import selectors from '@/redux/selectors/selectors';
import getTimeMachineEntry from '@/screens/workflows/redux/actions/getTimeMachineEntry';
import findFirstFailedTimeMachineIndex from '@/screens/workflows/workflowLogs/lib/findFirstFailedTimeMachineIndex';
import getLogTimeMachine from '@/screens/workflows/workflowLogs/lib/getLogTimeMachine';
import { useMemo } from 'react';

const getDetailsWorkflowError = (log) => {
  if (!log) {
    return null;
  }

  switch (log.statusId) {
    case 3:
      return {
        title: 'Workflow Failed',
        message: log.summary || 'Something went wrong while running the workflow'
      };
    case 4:
      return {
        title: 'Workflow Cancelled',
        message: 'The workflow was cancelled by the user'
      };
    case 5:
      if (log.summary === 'Workflow runner process crashed') {
        return {
          title: 'Workflow Runner Process Crashed',
          message: 'The server running this workflow had its process crash. This is almost always due to running out of memory. Please consider making the workflow run for a shorter period or handle data more efficiently.'
        };
      }
      return {
        title: 'Workflow Interrupted',
        message: log.summary || 'Something has interrupted the workflow while it was running'
      };
    case 6:
      return {
        title: 'Operation Limit Exceeded',
        message: 'Your account has reached the maximum number of operations. Please upgrade your account to continue.'
      };
    default:
      return null;
  }
};

const DetailsError = () => {
  const activeLogId = useArgSelector(selectors.query, 'logId');
  const log = useArgSelector(selectors.workflowLogsById, activeLogId);
  const workflowError = getDetailsWorkflowError(log);

  const lastWorkDoneErrors = useArgSelector(selectors.workflowLogOperationLogErrors, activeLogId);
  const lastWorkDoneError = lastWorkDoneErrors?.[lastWorkDoneErrors?.length - 1];
  const step = useArgSelector(selectors.workflowLogStepOutputByStepId, activeLogId, lastWorkDoneError?.stepId);
  const loading = useArgSelector(selectors.isLoading, 'getWorkflowLogOutput');

  const output = useArgSelector(selectors.workflowLogOutputById, activeLogId);
  const operationLogs = useArgSelector(selectors.workflowOperationLogsByLogId, activeLogId);
  const triggerStep = log?.meta?.triggerStep;
  // Same trigger inputs as the log viewer, so indexes here line up with the viewer's `time`.
  const invokedBy = log?.meta?.invokedBy;
  const manualUserId = invokedBy?.startsWith('manual-') ? invokedBy.split('-')[1] : null;
  const isCrashed = log?.statusId === 5;
  const isCancelled = log?.statusId === 4;

  const stepErrorTimeEntry = useMemo(() => {
    if (!lastWorkDoneError || !output) {
      return null;
    }
    return getTimeMachineEntry({ logId: activeLogId, operationLog: lastWorkDoneError });
  }, [activeLogId, lastWorkDoneError, output]);

  const workflowErrorTimeMachine = useMemo(() => {
    if (!workflowError || !output) {
      return null;
    }
    const { timeMachine } = getLogTimeMachine({
      output,
      operationLogs,
      time: 0,
      search: null,
      triggerStep,
      invokedBy,
      manualUserId,
      incomplete: isCancelled || isCrashed
    });
    return timeMachine?.length > 0 ? { timeMachine, lastIndex: timeMachine.length - 1 } : null;
  }, [workflowError, output, operationLogs, triggerStep, invokedBy, manualUserId, isCancelled, isCrashed]);

  const handleView = () => {
    if (stepErrorTimeEntry?.index !== undefined) {
      setQuery({ logStepId: lastWorkDoneError?.stepId, time: stepErrorTimeEntry.index, logTab: 'timeline', scrollTo: lastWorkDoneError?.stepId });
      return;
    }

    if (workflowErrorTimeMachine) {
      const { timeMachine, lastIndex } = workflowErrorTimeMachine;
      // The failing action is the last one only in a sequential run. A parallel loop finishes
      // its in-flight items after a failure, so look for the earliest failed action first.
      const firstFailedIndex = findFirstFailedTimeMachineIndex(timeMachine);
      const targetIndex = firstFailedIndex !== -1 ? firstFailedIndex : lastIndex;
      const targetEntry = timeMachine[targetIndex];
      const targetStepId = targetEntry?.stepId;

      if (targetStepId) {
        setQuery({ logStepId: targetStepId, time: targetIndex, logTab: 'timeline', scrollTo: targetStepId });
      } else {
        setQuery({ time: targetIndex, logTab: 'timeline' });
      }
    }
  };

  const hasValidTimeEntry = stepErrorTimeEntry?.index !== undefined || workflowErrorTimeMachine !== null;

  const title = workflowError ? workflowError?.title : `Error occurred in step ${step?.stepNumber} - ${step?.message}`;
  const message = workflowError ? workflowError?.message : lastWorkDoneError?.message;
  const isLongMessage = typeof message === 'string' && message.length > 300;

  if (!workflowError && (!lastWorkDoneError || loading)) {
    return null;
  }

  return (
    <Page.Section variant="chromeless" className={`details-error ${isLongMessage ? 'long-message' : ''}`}>
      <IconV2 icon={FiAlertCircle} size={18} />
      <div className="error-content" data-title={message}>
        {title && <div className="error-title">{title}</div>}
        {message && <div className="error-message">{message}</div>}
      </div>
      {hasValidTimeEntry && (
        <Button className="outline" onClick={handleView}>
          View Error
        </Button>
      )}
    </Page.Section>
  );
};

export default DetailsError;
